import { useCallback, useEffect, useState } from 'react';
import type { AiBudget, HealthReport, UsageSummary } from '../../types/aiOps';
import {
  aiOpsErrorMessage,
  AiOpsUnavailableError,
  fetchBudget,
  fetchHealth,
  fetchUsage,
  runHealthCheck,
  saveBudget as saveBudgetRequest,
} from './aiOpsApi';

/** The last stored check results, and the button that runs them again now */
export function useProviderHealth(enabled: boolean) {
  const [report, setReport] = useState<HealthReport | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    fetchHealth()
      .then((next) => {
        if (!cancelled) setReport(next);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof AiOpsUnavailableError) setUnavailable(true);
        else setError(aiOpsErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const check = useCallback(async (): Promise<boolean> => {
    setChecking(true);
    setError(null);
    try {
      const next = await runHealthCheck();
      setReport(next);
      setUnavailable(false);
      return true;
    } catch (err) {
      if (err instanceof AiOpsUnavailableError) setUnavailable(true);
      else setError(aiOpsErrorMessage(err));
      return false;
    } finally {
      setChecking(false);
    }
  }, []);

  return { report, loading, checking, error, unavailable, check };
}

/** One month of spend; changing the month refetches */
export function useUsage(month: string, enabled: boolean, reloadToken = 0) {
  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchUsage(month)
      .then((next) => {
        if (!cancelled) setUsage(next);
      })
      .catch((err) => {
        if (cancelled) return;
        setUsage(null);
        if (err instanceof AiOpsUnavailableError) setUnavailable(true);
        else setError(aiOpsErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [month, enabled, reloadToken]);

  return { usage, loading, error, unavailable };
}

export function useBudget(enabled: boolean) {
  const [budget, setBudget] = useState<AiBudget | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);
    fetchBudget()
      .then((next) => {
        if (!cancelled) setBudget(next);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof AiOpsUnavailableError) setUnavailable(true);
        else setError(aiOpsErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const save = useCallback(async (next: AiBudget): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      const saved = await saveBudgetRequest(next);
      setBudget(saved);
      return true;
    } catch (err) {
      setError(aiOpsErrorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { budget, loading, saving, error, unavailable, save };
}
